import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getStripeEnvironment } from "@/lib/stripe";

// Stripe-aligned tiers. Portal gates that historically said
// "starter/growth/enterprise" are mapped via TIER_RANK below.
export type SubscriptionTier = "none" | "essential" | "growth" | "dominate" | "titan";

// Backward-compat aliases used by existing <SubscriptionGate> usages
type LegacyTier = "starter" | "growth" | "enterprise";
type AnyTier = SubscriptionTier | LegacyTier;

const TIER_RANK: Record<AnyTier, number> = {
  none: 0,
  // legacy
  starter: 1,
  growth: 2,
  enterprise: 3,
  // current
  essential: 1,
  dominate: 3,
  titan: 4,
};

const TIER_LABEL: Record<SubscriptionTier, string> = {
  none: "No Active Plan",
  essential: "Neil AI Essential",
  growth: "Neil AI Growth",
  dominate: "Neil AI Dominate",
  titan: "Neil AI Titan",
};

export type SubscriptionRow = {
  id: string;
  tier: SubscriptionTier | null;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_subscription_id: string;
  stripe_customer_id: string;
};

interface SubscriptionContextType {
  currentTier: SubscriptionTier;
  tierLevel: number;
  tierLabel: string;
  subscription: SubscriptionRow | null;
  isActive: boolean;
  loading: boolean;
  hasAccess: (requiredTier: AnyTier) => boolean;
  refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

function isRowActive(row: SubscriptionRow | null): boolean {
  if (!row) return false;
  const futureEnd =
    !row.current_period_end || new Date(row.current_period_end).getTime() > Date.now();
  if (["active", "trialing", "past_due"].includes(row.status) && futureEnd) return true;
  if (row.status === "canceled" && futureEnd) return true;
  return false;
}

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    let environment: "sandbox" | "live" = "sandbox";
    try {
      environment = getStripeEnvironment();
    } catch {
      // payments not configured yet — fall back to sandbox so dev still queries
    }
    const { data, error } = await supabase
      .from("subscriptions")
      .select(
        "id, tier, status, current_period_end, cancel_at_period_end, stripe_subscription_id, stripe_customer_id",
      )
      .eq("user_id", user.id)
      .eq("environment", environment)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("subscription fetch error:", error);
      setSubscription(null);
    } else {
      setSubscription((data as SubscriptionRow) ?? null);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Realtime updates when webhook writes to the row
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`subscriptions:${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => fetchSubscription(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchSubscription]);

  const isActive = isRowActive(subscription);
  const currentTier: SubscriptionTier =
    isActive && subscription?.tier ? (subscription.tier as SubscriptionTier) : "none";
  const tierLevel = TIER_RANK[currentTier];
  const tierLabel = TIER_LABEL[currentTier];

  const hasAccess = (requiredTier: AnyTier) => {
    const requiredLevel = TIER_RANK[requiredTier] ?? 0;
    return tierLevel >= requiredLevel;
  };

  return (
    <SubscriptionContext.Provider
      value={{
        currentTier,
        tierLevel,
        tierLabel,
        subscription,
        isActive,
        loading,
        hasAccess,
        refetch: fetchSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};
